<template>
	<!-- this component is used in the components/purchase/index.vue for in game case (route: /checkout/in-game/purchase) -->
	<div class="ingame-summary">
		<div class="ingame-summary__title">{{ $t('checkout.purchase-summary') }}</div>

		<div class="ingame-summary__items">
			<div
				v-for="(item, idx) in products"
				:key="item.productId"
				class="ingame-summary__items__item"
				:class="`ingame-summary__items__item--${item.productType}`"
			>
				<div class="ingame-summary__items__item__name">
					<div class="ingame-summary__items__item__name__title">{{ $t('item') }}</div>
					<div class="ingame-summary__items__item__name__value">
						{{
							item.productType && item.productType === 'nxwallet'
								? `${$n(item.value)} NX`
								: item.productType === 'wallet'
								? $n(item.amount)
								: item.name
						}}
					</div>
				</div>
				<div class="ingame-summary__items__item__qty">
					<div class="ingame-summary__items__item__qty__title">{{ $t('checkout.qty') }}</div>
					<div class="ingame-summary__items__item__qty__value">{{ item.quantity }}</div>
				</div>
				<div class="ingame-summary__items__item__price">
					<div class="ingame-summary__items__item__price__title">{{ $t('checkout.price') }}</div>
					<currency
						class="ingame-summary__items__item__price__value"
						:code="item.currency"
						:value="item.amount"
					/>
				</div>
				<div v-if="idx < products.length - 1" class="ingame-summary__items__item__separator" />
			</div>
			<div v-if="purchaseSummary.mileageAmount" class="ingame-summary__items__item__mileage">
				<div class="ingame-summary__items__item__multi__separator" />
				<div class="ingame-summary__items__item__mileage__title__wrapper">
					<div class="ingame-summary__items__item__mileage__title">
						{{ $t('mileage.use', { currency: mileage.coinName }) }}
					</div>
					<span>{{ mileage.unitName }}{{ purchaseSummary.mileageAmount }}</span>
				</div>
			</div>
		</div>

		<div v-if="region === 'global' || region === 'tw'" class="ingame-summary__price">
			<div v-if="showSalesTax" class="ingame-summary__price__tax">
				<span class="ingame-summary__price__tax__title">{{ $t('checkout.sales-tax') }}</span>
				<currency class="ingame-summary__price__tax__value" :code="currency" :value="tax" />
			</div>
			<div class="ingame-summary__price__total">
				<span class="ingame-summary__price__total__title">{{ $t('checkout.total') }}</span>
				<currency class="ingame-summary__price__total__value" :code="currency" :value="total" />
			</div>
		</div>

		<div v-else class="ingame-summary__details">
			<div class="ingame-summary__details__order-total">
				<span class="ingame-summary__details__order-total__title">{{ $t('checkout.order-total') }}</span>
				<currency
					class="ingame-summary__details__order-total__value"
					:code="currency"
					:value="purchaseSummary.subTotalAmount"
				/>
			</div>

			<div
				v-show="
					!(['sea', 'th'].includes(region) && !['wallet', 'nxwallet'].includes(purchaseSummary.productType))
				"
				class="ingame-summary__details__amount-balance"
			>
				<span class="ingame-summary__details__amount-balance__title">
					{{ $t('checkout.amount-from-balance') }}
				</span>
				<currency
					v-if="purchaseSummary.availableBalance"
					class="ingame-summary__details__amount-balance__value"
					:code="purchaseSummary.availableBalance.currency"
					:value="purchaseSummary.availableBalance.amount"
				/>
			</div>

			<div
				v-show="
					!(['sea', 'th'].includes(region) && !['wallet', 'nxwallet'].includes(purchaseSummary.productType))
				"
				class="ingame-summary__details__amount-due"
			>
				<span class="ingame-summary__details__amount-due__title">{{ $t('checkout.amount-due') }}</span>
				<div class="ingame-summary__details__amount-due__value">
					({{ $t('checkout.insufficient-balance') }})
					<currency :code="currency" :value="total" />
				</div>
			</div>
		</div>

		<License
			:style="{
				paddingTop: '16px',
				textAlign: 'left',
				borderTop: '2px solid rgba(255, 255, 255, 0.5)',
			}"
			@termClick="handleTermClick"
			@supportClick="handleSupportClick"
		/>
	</div>
</template>

<script>
import summaryMixin from '@/components/purchase/summary-mixin';
import currency from '@/components/common/currency';
import License from '@/components/common/license.vue';

export default {
	name: 'InGamePurchaseSummary',
	components: {
		currency,
		License,
	},
	mixins: [summaryMixin],
	data() {
		return {};
	},
	computed: {
		isMapleStoryWorld() {
			const MAPLE_STORY_WORLDS_SERVICE_CODES = ['S59797', 'S59796', 'S59794', 'S90000'];
			return MAPLE_STORY_WORLDS_SERVICE_CODES.includes(this.serviceCode);
		},
	},
	methods: {
		handleTermClick() {
			this.$a2s.click('location_terms');
		},
		handleSupportClick() {
			this.$a2s.click('location_support');
		},
		supportTracking() {
			dataLayer.push({
				event: 'In Game Purchase Support Click',
			});
		},
	},
};
</script>

<style lang="scss" scoped>
.ingame-summary {
	flex: 0 0 100%;
	max-width: 100%;
	padding: 13px 20px;
	border: 1px solid rgba(#fff, 0.2);
	border-radius: 2px;
	background-color: rgba(#fff, 0.05);

	&__title {
		font-size: 18px;
		font-weight: bold;
		color: #fff;
		padding-bottom: 16px;
		border-bottom: 2px solid rgba(#fff, 0.5);
	}

	&__items {
		margin-top: 12px;
		margin-bottom: 12px;
		padding: 16px;
		border-radius: 1px;
		background-color: rgba(255, 255, 255, 0.05);

		&__item {
			display: flex;
			flex-direction: column;
			gap: 16px;
			width: 100%;
			line-height: 18px;
			color: #fff;

			font-size: 14px;
			color: rgba(255, 255, 255, 0.5);
			font-weight: 400;

			$self: &;

			&__name,
			&__qty,
			&__mileage,
			&__price {
				display: flex;
				justify-content: space-between;
				align-items: center;

				&__title,
				&__value {
					display: block;
				}

				&__title {
					width: 1px;
					white-space: nowrap;
					padding-right: 10px;
				}

				&__value {
					color: rgba(255, 255, 255, 0.8);
				}
			}

			&__multi__separator {
				height: 1px;
				background-color: rgba(#fff, 0.05);
				margin-top: 20px;
				margin-bottom: 20px;
			}

			&__separator {
				height: 1px;
				background-color: rgba(#fff, 0.05);
				margin-top: 4px;
				margin-bottom: 20px;
			}

			&__mileage {
				display: block;

				&__title__wrapper {
					display: flex;
					justify-content: space-between;
					align-items: center;
					color: white;
					font-size: 14px;
					font-weight: 700;
				}

				&__title {
					font-weight: 400;
				}
			}

			&__price__value {
				font-weight: 700;
				color: white;
			}

			&#{$self}--nxwallet,
			&#{$self}--wallet {
				display: flex;
				justify-content: space-between;
				align-items: center;
				font-weight: bold;
				padding-top: 10px;
				padding-bottom: 9px;

				#{$self}__qty {
					display: none;
				}

				#{$self}__name,
				#{$self}__price {
					display: inline;
					&__title {
						display: none;
						padding: 0;
					}
					&__value {
						font-size: 18px;
						display: inline;
						padding: 0;
					}
				}

				#{$self}__price {
					margin-left: auto;
				}

				#{$self}__name {
					&__value {
						display: flex;
						align-items: center;

						&:before {
							content: '';
							display: inline-block;
							width: 30px;
							height: 30px;
							background-repeat: no-repeat;
							background-size: contain;
							background-position: left center;
							margin-right: 10px;
						}
					}
				}
			}

			&#{$self}--nxwallet {
				#{$self}__name {
					&__value {
						&:before {
							background-image: url('~@/images/nx.png');
						}
					}
				}
			}

			&#{$self}--wallet {
				#{$self}__name {
					&__value {
						&:before {
							background-image: url('~@/images/coin.png');
						}
					}
				}
			}
		}
	}

	&__change-amount {
		position: relative;
		padding: 6px 0 0 0;
		text-transform: uppercase;
	}
	&__price {
		margin-left: auto;
		display: table;
		margin-bottom: 16px;
		padding-top: 6px;

		&__tax,
		&__total {
			display: table-row;

			&__title,
			&__value {
				display: table-cell;
				text-align: right;
			}

			&__value {
				padding-left: 19px;
				font-size: 24px;
				font-weight: bold;
				color: #fff;
			}
		}

		&__tax {
			&__title,
			&__value {
				padding-bottom: 16px;
			}
		}
	}

	&__details {
		width: 100%;
		padding-bottom: 18px;
		&__order-total,
		&__amount-balance,
		&__amount-due {
			display: flex;
			flex-wrap: nowrap;
			padding-top: 14px;
			&__title {
				padding-right: 20px;
			}
			&__value {
				color: #fff;
				margin-left: auto;
				text-align: right;
			}
		}

		&__order-total {
			padding-top: 5px;
		}

		&__order-total,
		&__amount-balance {
			&__value {
				white-space: nowrap;
			}
		}

		&__amount-due {
			&__title {
				flex-grow: 1;
				flex-shrink: 0;
				padding-right: 30px;
			}
			&__value {
				color: #ff1744;
				flex-shrink: 1;
				white-space: normal;
			}
		}
	}

	&__message {
		border-top: 2px solid rgba(#fff, 0.5);
		text-align: left;
		padding-top: 16px;
		&__support {
			margin-bottom: 3px;
			a {
				text-transform: capitalize;
			}
		}
		p {
			line-height: 1.3;
		}
		a {
			color: inherit;
			text-decoration: underline;
			text-underline-position: under;
			&:hover {
				color: #fff;
			}
		}
	}
}

.attention {
	color: #fff;
	font-family: Arial;
	font-size: 14px;
	font-style: normal;
	font-weight: 700;
	line-height: normal;
}

.highlight {
	color: rgba(230, 63, 87, 1);
	font-family: Arial;
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	line-height: normal;
}

.license-text {
	color: rgba(#fff, 0.5);
	margin: 10px 0;
	font-family: Arial;
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	line-height: normal;
	margin: 10px 0;
	.license-text + .license-text {
		margin: 0;
	}
}

@media (min-width: 1024px) {
	.ingame-summary {
		min-height: 453px;
	}
}

@media (max-width: 599px) {
	.ingame-summary {
		padding: 8px 10px;
		&__title {
			padding-bottom: 11px;
		}

		&__items {
			&__item {
				$self: &;

				&#{$self}--nxwallet,
				&#{$self}--wallet {
					font-size: 14px;
				}
			}
		}

		&__price {
			display: flex;
			flex-wrap: wrap;
			margin-bottom: 18px;
			padding-top: 14px;

			&__tax,
			&__total {
				display: flex;
				flex-wrap: wrap;
				flex: 0 0 100%;
				max-width: 100%;
				justify-content: flex-end;
				align-items: flex-end;

				&__title,
				&__value {
					display: block;
					padding-right: 20px;
					padding-bottom: 0px;
				}
				&__value {
					padding: 0px;
				}
			}
			&__tax {
				padding-bottom: 16px;
			}
		}

		&__details {
			&__order-total {
				padding-top: 14px;
			}
		}

		&__message {
			padding-bottom: 5px;
		}
	}
}
</style>
